/**
 * Which regional sign-in and room-sharing options each country sees.
 *
 * THIS IS THE ONE PLACE TO CHANGE IT. Components never test a country
 * themselves; they ask getRegionalAuthProviders / getRegionalShareProviders.
 * Flip a provider to false (or delete the row) and every surface follows.
 *
 * Two separate systems on purpose: sharing through LINE does not need a LINE
 * account, and a Kakao account can share through anything.
 *
 * Country is presentation only. It decides what is shown first, never what is
 * allowed: every provider stays reachable behind "More options", and the
 * server never looks at the country when someone signs in. A Korean player in
 * Germany still gets to their Kakao account.
 *
 * Mapping researched 2026-10-08 (official provider docs, Similarweb 2025
 * messaging study, Sinch, Meduza on Russian blocks):
 *  - KR: KakaoTalk is #1; Naver is the other account Koreans already have.
 *  - JP, TW, TH: LINE is #1, and LINE Login runs channels for these regions.
 *  - RU: WhatsApp is blocked (Nov 2025 onward) and Telegram is mostly blocked
 *    but still used through VPNs; VK is the domestic network with VK ID.
 *  - BY, KZ: Telegram leads, VK is widely held.
 *  - UA: Telegram leads; VK is banned there, so no VK.
 *  - UZ, IR: Telegram leads.
 *  - US: WhatsApp is a minority app next to iMessage/SMS, which the phone's
 *    own share sheet already covers.
 *  - Everyone else: WhatsApp, which is what the invite card showed before.
 *  - VN (Zalo) and CN (WeChat) have no supported web share link, so they get
 *    the share sheet and Copy link only.
 */

export const REGIONAL_AUTH_PROVIDERS = ['kakao', 'naver', 'line', 'vk'] as const;
export type RegionalAuthProvider = typeof REGIONAL_AUTH_PROVIDERS[number];

/** Dedicated share buttons. Native share, copy link and copy code are always offered separately. */
export const SHARE_PROVIDERS = ['kakao', 'line', 'vk', 'telegram', 'whatsapp', 'sms', 'discord'] as const;
export type ShareProvider = typeof SHARE_PROVIDERS[number];

/** Everything a share action can be, for analytics. */
export type ShareAction = ShareProvider | 'native' | 'copy_link' | 'copy_code';

type ProviderSwitches<T extends string> = Partial<Record<T, boolean>>;

export interface RegionalMarket {
  auth?: ProviderSwitches<RegionalAuthProvider>;
  share?: ProviderSwitches<ShareProvider>;
}

/** ISO 3166-1 alpha-2 -> what that country sees first. Order inside a row is display order. */
export const REGIONAL_MARKETS: Readonly<Record<string, RegionalMarket>> = {
  KR: { auth: { kakao: true, naver: true }, share: { kakao: true } },
  JP: { auth: { line: true }, share: { line: true } },
  TW: { auth: { line: true }, share: { line: true } },
  TH: { auth: { line: true }, share: { line: true } },
  RU: { auth: { vk: true }, share: { vk: true, telegram: true } },
  BY: { auth: { vk: true }, share: { telegram: true, vk: true } },
  KZ: { auth: { vk: true }, share: { telegram: true, vk: true } },
  UA: { share: { telegram: true } },
  UZ: { share: { telegram: true } },
  IR: { share: { telegram: true } },
  US: { share: {} },
  VN: { share: {} },
  CN: { share: {} },
};

/**
 * Which regional sign-ins are open to EVERYONE (once the regional_sign_in
 * flag is published), per provider and country. Anything not listed here
 * stays tester-only even with the flag published, so each provider can go
 * live country by country after it has been tested there.
 *
 * 2026-10-08 (owner's call, before any team member had completed a sign-in):
 * LINE opened in Japan, Taiwan and Thailand, Kakao in Korea (its client
 * secret was checked against Kakao's token endpoint). NAVER stays
 * tester-only until NAVER's own review approves the app: until then NAVER
 * itself refuses everyone except the app's registered members. VK ID has no
 * app yet.
 */
export const PUBLIC_SIGN_IN: Readonly<Partial<Record<RegionalAuthProvider, readonly string[]>>> = {
  line: ['JP', 'TW', 'TH'],
  kakao: ['KR'],
};

/** Whether `provider` is open to everyone in `country` (not just testers). */
export function isPublicSignIn(provider: RegionalAuthProvider, country: string | null | undefined): boolean {
  const code = normalizeCountry(country);
  return !!code && (PUBLIC_SIGN_IN[provider] ?? []).includes(code);
}

/** Providers open to everyone somewhere: what a non-tester can reach under "More sign-in options". */
export function getPublicSignInProviders(): RegionalAuthProvider[] {
  return REGIONAL_AUTH_PROVIDERS.filter((p) => (PUBLIC_SIGN_IN[p]?.length ?? 0) > 0);
}

/** Countries without a row (and an unknown country) get this. */
export const DEFAULT_SHARE: ProviderSwitches<ShareProvider> = { whatsapp: true };

/**
 * ipapi / timezone-fallback spellings (see country-from-timezone.ts) for the
 * countries above. Only these need a name: any other country falls through
 * to the default row, which is the same answer its code would give.
 */
const COUNTRY_NAME_TO_CODE: Readonly<Record<string, string>> = {
  'south korea': 'KR',
  'korea, republic of': 'KR',
  'republic of korea': 'KR',
  japan: 'JP',
  taiwan: 'TW',
  thailand: 'TH',
  russia: 'RU',
  'russian federation': 'RU',
  belarus: 'BY',
  kazakhstan: 'KZ',
  ukraine: 'UA',
  uzbekistan: 'UZ',
  iran: 'IR',
  vietnam: 'VN',
  'viet nam': 'VN',
  china: 'CN',
  'united states': 'US',
};

/**
 * Normalises whatever the site knows about a visitor's country to an ISO
 * code, or null. Accepts an ISO code ("kr") or an ipapi name ("South Korea").
 */
export function normalizeCountry(country: string | null | undefined): string | null {
  if (!country) return null;
  const trimmed = country.trim();
  if (/^[A-Za-z]{2}$/.test(trimmed)) return trimmed.toUpperCase();
  return COUNTRY_NAME_TO_CODE[trimmed.toLowerCase()] ?? null;
}

function enabledKeys<T extends string>(switches: ProviderSwitches<T> | undefined): T[] {
  if (!switches) return [];
  return (Object.keys(switches) as T[]).filter((key) => switches[key] === true);
}

/** Regional sign-in providers to show up front for this country. Empty for most of the world. */
export function getRegionalAuthProviders(country: string | null | undefined): RegionalAuthProvider[] {
  const code = normalizeCountry(country);
  if (!code) return [];
  return enabledKeys(REGIONAL_MARKETS[code]?.auth).filter((p) => REGIONAL_AUTH_PROVIDERS.includes(p));
}

/** Dedicated share buttons to show up front for this country (beside native share + copy). */
export function getRegionalShareProviders(country: string | null | undefined): ShareProvider[] {
  const code = normalizeCountry(country);
  const row = code ? REGIONAL_MARKETS[code] : undefined;
  const switches = row?.share ?? DEFAULT_SHARE;
  return enabledKeys(switches).filter((p) => SHARE_PROVIDERS.includes(p));
}

/**
 * Sign-in options for the "More sign-in options" fallback: every configured
 * regional provider not already shown. This is how a traveller reaches the
 * account they made at home.
 */
export function getOtherAuthProviders(
  shown: readonly RegionalAuthProvider[],
  available: readonly RegionalAuthProvider[] = REGIONAL_AUTH_PROVIDERS,
): RegionalAuthProvider[] {
  return REGIONAL_AUTH_PROVIDERS.filter((p) => available.includes(p) && !shown.includes(p));
}

/** Share buttons for "More ways to share": everything not already up front. */
export function getOtherShareProviders(shown: readonly ShareProvider[], options: { touch: boolean }): ShareProvider[] {
  // Discord is never in "More": desktop visitors get it up front (see RoomShareActions), phones do not.
  return SHARE_PROVIDERS.filter((p) => !shown.includes(p) && p !== 'discord' && (p !== 'sms' || options.touch));
}

/**
 * The sign-in providers this device should show up front: the country's
 * providers plus the one this browser last signed in with, so someone who
 * signed up with LINE in Tokyo still sees LINE on a trip to London.
 */
export function getPrimaryAuthProviders(
  country: string | null | undefined,
  lastUsed: RegionalAuthProvider | null,
  available: readonly RegionalAuthProvider[],
  options: { publicOnly?: boolean } = {},
): RegionalAuthProvider[] {
  // A non-tester sees only what is open in their country; the provider they
  // already signed in with stays (they have that account, wherever they are).
  const list = getRegionalAuthProviders(country).filter((p) => !options.publicOnly || isPublicSignIn(p, country));
  if (lastUsed && !list.includes(lastUsed)) list.unshift(lastUsed);
  return list.filter((p) => available.includes(p));
}

/**
 * Everything a viewer can sign in with: testers get every configured
 * provider, everyone else only the ones open somewhere (PUBLIC_SIGN_IN) plus
 * the one this device already used.
 */
export function getReachableAuthProviders(
  available: readonly RegionalAuthProvider[],
  options: { publicOnly?: boolean; lastUsed?: RegionalAuthProvider | null } = {},
): RegionalAuthProvider[] {
  if (!options.publicOnly) return [...available];
  const open = getPublicSignInProviders();
  return available.filter((p) => open.includes(p) || p === options.lastUsed);
}

export function isRegionalAuthProvider(value: unknown): value is RegionalAuthProvider {
  return typeof value === 'string' && (REGIONAL_AUTH_PROVIDERS as readonly string[]).includes(value);
}

export function isShareAction(value: unknown): value is ShareAction {
  return typeof value === 'string'
    && ((SHARE_PROVIDERS as readonly string[]).includes(value) || value === 'native' || value === 'copy_link' || value === 'copy_code');
}

/** Display names. Brand names are not translated. */
export const PROVIDER_LABELS: Readonly<Record<RegionalAuthProvider | ShareProvider, string>> = {
  kakao: 'Kakao',
  naver: 'NAVER',
  line: 'LINE',
  vk: 'VK ID',
  telegram: 'Telegram',
  whatsapp: 'WhatsApp',
  sms: 'SMS',
  discord: 'Discord',
};

export const SHARE_LABELS: Readonly<Record<ShareProvider, string>> = {
  kakao: 'KakaoTalk',
  line: 'LINE',
  vk: 'VK',
  telegram: 'Telegram',
  whatsapp: 'WhatsApp',
  sms: 'SMS',
  discord: 'Discord',
};

/** Feature flags (feature_flags/{id}); both ship tester-only. */
export const REGIONAL_SIGN_IN_FLAG = 'regional_sign_in';
export const REGIONAL_SHARE_FLAG = 'regional_share';
